import type { RecordJobData } from "@smartsuite/automation-engine-api";
import { ActivityDetailsTriggerGroup } from "../../../../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";
import { getRecordDataId } from "./utils";
import type { ReactNode } from "react";

interface WithFindWrapperProps {
  recordJobData?: RecordJobData;
  recordJobDataByDetailsId: Record<string, RecordJobData>;
  /**
   * The action's own record jobs, when it fanned out into more than one -- a
   * record-list action inside a Conditional Logic branch, whose records are
   * SIBLINGS of the fetched one (see NestedGroupsRenderer). Only used when the
   * fetched record job created no records of its own, so the update-after-find
   * shape below keeps addressing its records as children (BUG-21290).
   */
  recordJobIds?: string[];
  onRecordClick?: (detailsId: string) => void;
  children:
    | ((props: { recordJobData: RecordJobData }) => JSX.Element | null)
    | ReactNode;
}

export const WithFindWrapper = ({
  recordJobData,
  recordJobDataByDetailsId,
  recordJobIds,
  onRecordClick,
  children,
}: WithFindWrapperProps): JSX.Element | null => {
  if (!recordJobData) {
    return null;
  }
  const recordsCount = recordJobData?.recordJobCreatedCount;

  const recordDataIds = recordsCount
    ? getRecordDataId(recordJobData.recordJobId, recordsCount)
    : (recordJobIds ?? []);

  if (recordDataIds.length === 0) {
    return typeof children === "function"
      ? children({ recordJobData })
      : (children as JSX.Element);
  }

  return (
    <>
      {recordDataIds.map((recordDataId, index) => {
        if (!recordDataId) return null;

        const recordJobData = recordJobDataByDetailsId[recordDataId];

        return (
          <ActivityDetailsTriggerGroup
            key={recordDataId}
            title={`Record ${index + 1}`}
            onClick={() => onRecordClick?.(recordDataId)}
          >
            {typeof children === "function"
              ? children({ recordJobData })
              : (children as JSX.Element)}
          </ActivityDetailsTriggerGroup>
        );
      })}
    </>
  );
};
